// Theme store — Zustand slice for light/dark mode.
// Pairs with the inline init script in index.html (no-FOUC).
// Dark mode uses the [data-theme="dark"] attribute on <html>
// (frontend-architecture.md §12.1, amended 2026-06-05 from the .dark class).

import { create } from 'zustand'

export type Theme = 'light' | 'dark'

interface ThemeState {
  theme: Theme
  toggle: () => void
  set: (theme: Theme) => void
}

function readInitial(): Theme {
  if (typeof document === 'undefined') return 'light'
  return document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light'
}

function applyToDom(theme: Theme): void {
  if (typeof document === 'undefined') return
  document.documentElement.setAttribute('data-theme', theme)
  try {
    localStorage.setItem('vysto.theme', theme)
  } catch {
    // localStorage unavailable; persist skipped.
  }
}

/**
 * Zustand light/dark store. Initial value reads the `data-theme` the no-FOUC
 * script already stamped; toggle/set write it back to <html> and persist to
 * localStorage 'vysto.theme'.
 */
export const useThemeStore = create<ThemeState>((set, get) => ({
  theme: readInitial(),
  toggle: () => {
    const next: Theme = get().theme === 'dark' ? 'light' : 'dark'
    applyToDom(next)
    set({ theme: next })
  },
  set: (theme) => {
    applyToDom(theme)
    set({ theme })
  },
}))
